import { useCallback, useMemo, useState } from 'react';

export interface UsePaginationOptions {
  perPage?: number;
  initialPage?: number;
}

export interface PaginationState<T> {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  pageItems: T[];
  setPage: (page: number) => void;
  next: () => void;
  prev: () => void;
  setPerPage: (perPage: number) => void;
  canNext: boolean;
  canPrev: boolean;
  startIndex: number;
  endIndex: number;
}

export function usePagination<T>(items: T[], options: UsePaginationOptions = {}): PaginationState<T> {
  const { perPage: initialPerPage = 25, initialPage = 1 } = options;
  const [page, setPageRaw] = useState(initialPage);
  const [perPage, setPerPageRaw] = useState(initialPerPage);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const safePage = Math.min(Math.max(1, page), totalPages);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return items.slice(start, start + perPage);
  }, [items, safePage, perPage]);

  const setPage = useCallback((p: number) => {
    setPageRaw(p);
  }, []);

  const next = useCallback(() => setPageRaw((p) => Math.min(totalPages, p + 1)), [totalPages]);
  const prev = useCallback(() => setPageRaw((p) => Math.max(1, p - 1)), []);

  const setPerPage = useCallback((count: number) => {
    setPerPageRaw(count);
    setPageRaw(1); // reset to first page when page size changes
  }, []);

  const startIndex = total === 0 ? 0 : (safePage - 1) * perPage + 1;
  const endIndex = Math.min(total, (safePage - 1) * perPage + pageItems.length);

  return {
    page: safePage,
    perPage,
    total,
    totalPages,
    pageItems,
    setPage,
    next,
    prev,
    setPerPage,
    canNext: safePage < totalPages,
    canPrev: safePage > 1,
    startIndex,
    endIndex,
  };
}
